'use client';

import type { ReactNode } from 'react';

/**
 * components/segmented.tsx — la bascule à N options, en matière.
 *
 * Grammaire de référence : le filtre du feed prestataire (Tout / Diagnostics /
 * Prioritaires). L'option choisie RESSORT — une bosse dans la matière du
 * feuillet, pas un bouton posé dessus.
 *
 * Trois conséquences, et ce sont elles qui font tout le procédé :
 *
 *  1. AUCUN fond, ni sur la pilule ni sur les options. La matière visible est
 *     celle du feuillet ; poser un `--s-1` dessous ajoutait un rectangle (vert
 *     pâle sous la palette ivory-green, où `--s-1` est teinté) qui cernait la
 *     bascule au lieu de la laisser affleurer.
 *  2. La bosse vit dans sa PROPRE couche, floutée à 2,5 px. Sans fond, il ne
 *     reste que les deux ombres — claire en haut-gauche, sombre en bas-droite —
 *     et le flou dissout leur contour, donc la couture qui trahirait un élément
 *     rapporté.
 *  3. Le libellé remonte de 1,5 px quand il est choisi : ce qui bombe porte son
 *     texte plus haut. C'est ce micro-décalage qui rend le relief crédible.
 *
 * `value` accepte une valeur hors liste (chaîne vide) : certaines bascules se
 * dé-sélectionnent au second clic (l'état d'une pièce, par exemple), auquel cas
 * aucune option ne ressort. C'est l'appelant qui décide, pas ce composant.
 *
 * L'option choisie BOMBE, partout, sans exception. J'ai essayé de la creuser
 * dans les conteneurs qui défilent pour contourner un rognage — c'était sortir
 * du sujet : la matière de l’app est la bosse, elle ne se négocie pas, et le
 * rognage se règle du côté du conteneur (cf. `.material-scroll` dans globals.css).
 */

export function Segmented<T extends string>({ options, value, onChange, className = '', icons }: {
  options: readonly (readonly [T, string])[];
  value: string;
  onChange: (v: T) => void;
  className?: string;
  /** Dessin par option, au-dessus du libellé. Facultatif : les bascules déjà
   *  posées ailleurs continuent d'afficher leur seul texte, sans rien changer
   *  chez elles. Là où il y en a, l'option se reconnaît d'un coup d'œil au lieu
   *  de se lire (demandé le 14/08 pour le mode d'intervention et le périmètre
   *  véhicule). La forme dit AVANT le mot — c'est tout l'intérêt. */
  icons?: Partial<Record<T, ReactNode>>;
}) {
  return (
    <div
      className={`relative grid items-center gap-1 rounded-[3px] p-[3px] ${className}`}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }}>
      {options.map(([id, label]) => (
        <button key={id} type="button" onClick={() => onChange(id)}
          className={`relative rounded-[6px] text-[11px] font-bold transition-colors ${
            icons ? 'py-2' : 'py-1.5'} ${
            value === id ? 'text-[var(--accent-ink)]' : 'text-[var(--ink-3)]'}`}>
          <span aria-hidden style={{ filter: 'blur(2.5px)' }}
            className={`absolute inset-0 rounded-[6px] shadow-[var(--sh-emboss)] transition-opacity ease-out ${
              value === id ? 'opacity-100 duration-300' : 'opacity-0 duration-700'}`} />
          {/* Le dessin monte avec le libellé quand l'option bombe : ce qui
              ressort porte TOUT son contenu plus haut, sinon l'icône reste
              collée au fond et le relief se défait. */}
          <span className={`relative flex flex-col items-center gap-1 px-1 transition-transform duration-300 ease-out ${
            value === id ? '-translate-y-[1.5px]' : 'translate-y-0'}`}>
            {icons?.[id] && <span aria-hidden className="block">{icons[id]}</span>}
            <span className="block truncate max-w-full">{label}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * LA BASCULE À PISTE, généralisée à N positions.
 *
 * `Segmented` fait ressortir l'option choisie ; ici c'est l'inverse — une piste
 * VERTE ET CREUSÉE dans laquelle glisse un curseur d'ivoire. La différence n'est
 * pas décorative : `Segmented` propose des commandes côte à côte, celle-ci
 * montre UNE position sur un parcours. Pour les colonnes du tableau de suivi,
 * qui sont un chemin qu'on remonte de gauche à droite, c'est la bonne forme —
 * une file de bosses les faisait lire comme cinq boutons sans rapport entre eux.
 *
 * RECETTE REPRISE TELLE QUELLE du bandeau de filtres du feed prestataire
 * (`Immédiat / Sur RDV`) : pourtour `--sh-emboss` flouté à 2,5 px, fond
 * `bg-emerald-500` (la CLASSE, jamais un `rgb()` recalculé — Tailwind v4 définit
 * ses couleurs en oklch et les deux ne donnent pas le même vert), creux
 * `--toggle-hollow` par-dessus le vert, curseur au papier du feuillet.
 *
 * LA PISTE A UNE TEINTE FIXE (`--toggle-track-a`, DA 10/10) ; LE CURSEUR, LUI,
 * SUIT LE FEUILLET SANS PASSER PAR REACT. Là-bas, `sheetAlpha` était
 * une variable de la page ; les deux pages publient déjà `--sheet-a` en CSS, on
 * la lit directement — la bascule s'efface avec le feuillet où qu'on la pose,
 * sans prop à faire descendre. Le curseur garde son cran de densité (+0,22) :
 * à opacité égale il paraît plus pâle, parce qu'il a du vert saturé derrière lui
 * et pas la carte.
 */
export function SlideToggle<T extends string>({ options, value, onChange, dots, icons, className = 'h-[38px]' }: {
  options: readonly (readonly [T, string])[];
  value: string;
  onChange: (v: T) => void;
  dots?: Partial<Record<T, boolean>>;
  /** Icône par option, au-dessus du libellé — même principe que
   *  `Segmented.icons` (la forme dit avant le mot), demandé le 22/09 pour la
   *  bascule Réseaux sociaux/Téléphone/E-mail/Clé de l'écran de connexion.
   *  Facultatif : les bascules déjà posées sans icônes n'ont rien à changer. */
  icons?: Partial<Record<T, ReactNode>>;
  /** Hauteur de la piste. Les bascules à deux mots (`Immédiat`/`Sur RDV`)
   *  tiennent en 38 px ; avec une icône au-dessus du mot il en faut plus
   *  (46 px dans le bandeau de filtres du feed prestataire, qui a servi de
   *  base à cette bascule). */
  className?: string;
}) {
  const n = options.length;
  // Valeur hors liste (chaîne vide) : aucune option choisie, le curseur
  // s'efface — même contrat que `Segmented` (l'état d'une pièce se dé-sélectionne).
  const choisi = options.findIndex(([id]) => id === value);
  const i = Math.max(0, choisi);

  return (
    <span className={`relative block ${className}`}>
      <span aria-hidden style={{ filter: 'blur(2.5px)' }}
        className="absolute inset-0 rounded-[10px] shadow-[var(--sh-emboss)]" />
      <span aria-hidden className="absolute inset-0 rounded-[10px] bg-emerald-500"
        style={{ opacity: 'var(--toggle-track-a)' }} />
      <span aria-hidden className="absolute inset-0 rounded-[10px] shadow-[var(--toggle-hollow)]" />

      {/* `(100% - 6px) / n` et non `100% / n` : le curseur doit rester DANS la
          piste avec le même jeu de 3 px aux deux bouts. Avec la division naïve,
          l'écart se cumulait à chaque position et le dernier cran débordait de
          3(n−1) pixels — invisible à deux positions, flagrant à six. */}
      <span aria-hidden
        className="absolute top-[3px] bottom-[3px] left-[3px] rounded-[8px] shadow-[var(--sh-card)]"
        style={{
          width: `calc((100% - 6px) / ${n})`,
          background: 'rgb(var(--s-sheet-rgb) / min(1, calc(var(--sheet-a, 1) + 0.22)))',
          transform: `translate3d(${i * 100}%, 0, 0)`,
          opacity: choisi < 0 ? 0 : 1,
          // La courbe du feuillet : rapide au départ, posée à l'arrivée. Aucun
          // dépassement — un curseur qui rebondit en bout de course a déjà été
          // signalé une fois (16/08).
          transition: 'transform .4s cubic-bezier(.16,1,.3,1)',
        }} />

      <span className="relative grid h-full" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))` }}>
        {options.map(([id, label], k) => {
          const actif = k === choisi;
          return (
            <button key={id} type="button" onClick={() => onChange(id)} aria-pressed={actif}
              className="relative min-w-0 flex flex-col items-center justify-center gap-0.5 px-0.5 rounded-[8px] transition-colors"
              // CE QUI EST ACTIF DOIT BRILLER, ET LE RESTE S'EFFACER. Blanc sur
              // vert, le côté éteint était le plus lumineux des deux et l'œil le
              // prenait pour le choix courant. La lueur n'existe que de nuit
              // (`--toggle-on-glow` vaut `none` de jour).
              style={actif
                ? { color: 'var(--toggle-on-ink)', filter: 'var(--toggle-on-glow)' }
                : { color: 'var(--toggle-off-ink)' }}>
              {dots?.[id] && (
                <span aria-label="En attente"
                  className="notif-dot absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,.9)]" />
              )}
              {icons?.[id] && <span aria-hidden className="shrink-0">{icons[id]}</span>}
              <span className="text-[9px] font-black leading-none truncate max-w-full">{label}</span>
            </button>
          );
        })}
      </span>
    </span>
  );
}
